import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, timeout } from 'rxjs';
import { ClientAnalyticsControllerService, ClientBrandTagResponse, ClientTopicsRequest, ClientTopicsResponse, ClientAnalyticsTopicRow } from 'api-clipping';
import { UtilsService } from '../../../../shared/services/utils.service';
import { ChartComponent } from '../../../../shared/components/sette/chart/chart.component';
import { KpiTileComponent } from '../../../../shared/components/sette/kpi-tile/kpi-tile.component';
import { SampleSizeNoteComponent } from '../../../../shared/components/sette/sample-size-note/sample-size-note.component';
import { AnalyticsSkeletonComponent } from '../../../../shared/components/sette/analytics-skeleton/analytics-skeleton.component';
import { AnalyticsErrorStateComponent, AnalyticsErrorKind } from '../../../../shared/components/sette/analytics-error-state/analytics-error-state.component';
import { AnalyticsEmptyStateComponent } from '../../../../shared/components/sette/analytics-empty-state/analytics-empty-state.component';
import { InfoIconComponent } from '../../../../shared/components/sette/info-icon/info-icon.component';
import { FilterState } from '../filter-bar/filter-bar.component';
import { buildAnalyticsFilterRequest, isCompareRequested, resolveComparison, classifyAnalyticsError, createAnalyticsFetchStream, ANALYTICS_REQUEST_TIMEOUT_MS, isNewMetric, newMetricLabel } from '../shared/analytics-request.util';
import { signTone, formatSigned, formatSignedAuto, formatAnalyticsNumber, formatAnalyticsInt, formatAnalyticsAxisDate, categoricalColors, brandColor, SENTIMENT_COLORS, AXIS, Tone } from '../../../../shared/components/sette/chart-theme';
import { translateTopic } from '../shared/taxonomy.util';

interface ChangeView {
	text: string;
	tone: Tone;
}

interface TopicRowView {
	name: string;
	mentions: number;
	hasEnoughData: boolean;
	change: ChangeView;
	reach: string;
	reachValue: number;
	impact: number;
	impactStr: string;
	impactTone: Tone;
	color: string;
}

interface NegativeTopicRowView {
	topic: string;
	negativePct: string;
	mentions: string;
}

// SETTE redesign (CLIPPING-598): topics have no fixed brand-style identity, so they take the
// categorical chart palette (avoiding chart-5, since this screen also shows Impact/sentiment).
const PALETTE = categoricalColors(13, { avoidSentimentClash: true });

// Ported from analytics-design/src/components/TopicsDashboard.tsx, then rewired to call
// POST /v1/client/analytics/topics instead of using sample data. The KPI row is reduced from 6
// cards to 4 (Topics Tracked / Avg Reach / Avg Impact / Net Sentiment) - the design's Mentions/
// Positive%/Negative%/Neutral% cards assumed a single "total mentions" denominator, which doesn't
// exist cleanly for topics (one clip can carry up to 4 topics at once). CLIPPING-5: Avg
// Reach/Avg Impact/Net Sentiment now come from the backend's own KPI row (see the getters below),
// computed once per tagged mention rather than pooled here from the topic rows, which is what used
// to double-count a multi-topic mention. "Top Negative Topics" drops its "vs prev" column - the
// backend doesn't track each topic's previous-period negative percentage, only current. "Topic
// Drilldown" keeps its mentions/reach/impact compare deltas (those ARE tracked per topic) but its
// sentiment donut shows current-period sentiment only, same reason as the dropped "vs prev" column
// above.
@Component({
	selector: 'app-topics',
	standalone: true,
	imports: [FormsModule, MatIconModule, TranslateModule, ChartComponent, KpiTileComponent, SampleSizeNoteComponent, AnalyticsSkeletonComponent, AnalyticsErrorStateComponent, AnalyticsEmptyStateComponent, InfoIconComponent],
	templateUrl: './topics.component.html',
	styleUrl: './topics.component.scss',
})
export class TopicsComponent implements OnChanges {
	@Input() filters!: FilterState;
	@Input() brandOptions: ClientBrandTagResponse[] = [];
	@Output() navigate = new EventEmitter<string>();

	loading = false;
	loadError = false;
	errorState: AnalyticsErrorKind = { kind: 'generic' };
	data: ClientTopicsResponse | null = null;

	showManageModal = false;
	timeTrendInterval: 'Daily' | 'Weekly' | 'Monthly' = 'Daily';
	selectedDrilldownTopic = '';
	private readonly fetchTrigger = new Subject<ClientTopicsRequest>();

	constructor(private readonly api: ClientAnalyticsControllerService, private readonly utils: UtilsService, private readonly translate: TranslateService) {
		// Задача 7: switchMap-based stale-response guard - see createAnalyticsFetchStream's doc.
		createAnalyticsFetchStream(this.fetchTrigger, (request) => this.api.getTopicsAnalytics(request).pipe(timeout(ANALYTICS_REQUEST_TIMEOUT_MS)))
			.pipe(takeUntilDestroyed())
			.subscribe((result) => {
				this.loading = false;
				if (result.ok) {
					this.data = result.data!;
					if (!this.selectedDrilldownTopic || !this.combinedRaw.some((t) => t.topic === this.selectedDrilldownTopic)) {
						this.selectedDrilldownTopic = this.combinedRaw[0]?.topic || '';
					}
				} else {
					this.loadError = true;
					this.errorState = classifyAnalyticsError(result.error);
				}
			});
	}

	get lang(): string {
		return this.utils.currentLanguage;
	}

	fmtInt(n: number): string {
		return formatAnalyticsInt(n, this.lang);
	}

	get showSkeleton(): boolean {
		return this.loading && !this.data;
	}

	get isRefreshing(): boolean {
		return this.loading && !!this.data;
	}

	get isEmptyPeriod(): boolean {
		return !this.loading && !this.loadError && (this.data?.mentions?.value ?? 0) === 0;
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['filters']) {
			this.fetch();
		}
	}

	fetch(): void {
		const fields = buildAnalyticsFilterRequest(this.filters);
		if (!fields) {
			return;
		}
		this.loading = true;
		this.loadError = false;
		this.fetchTrigger.next(fields as ClientTopicsRequest);
	}

	openManageModal(): void {
		this.showManageModal = true;
	}

	closeManageModal(): void {
		this.showManageModal = false;
	}

	addCustomTopic(): void {
		window.alert('Add custom topic modal available.');
	}

	onNavigate(targetId: string): void {
		this.navigate.emit(targetId);
	}

	private get combinedRaw(): ClientAnalyticsTopicRow[] {
		return [...(this.data?.industryTopics ?? []), ...(this.data?.corporateTopics ?? [])];
	}

	private row(t: ClientAnalyticsTopicRow, i: number): TopicRowView {
		return {
			name: translateTopic(t.topic, this.translate),
			mentions: t.mentions ?? 0,
			hasEnoughData: t.hasEnoughData ?? true,
			change: this.pctChange(t.mentions, t.previousMentions, t.hasEnoughCompareData),
			reach: t.avgReach != null ? formatAnalyticsNumber(t.avgReach, this.lang, 1) : '—',
			reachValue: t.avgReach ?? 0,
			impact: t.avgImpact ?? 0,
			impactStr: t.avgImpact != null ? formatSigned(t.avgImpact, 1, this.lang) : '—',
			impactTone: signTone(t.avgImpact ?? 0),
			color: PALETTE[i % PALETTE.length],
		};
	}

	toneClasses(tone: Tone): string {
		if (tone === 'positive') return 'bg-positive-tint text-positive';
		if (tone === 'negative') return 'bg-negative-tint text-negative';
		return 'bg-neutral-tint text-ink-subtle';
	}

	toneTextClass(tone: Tone): string {
		if (tone === 'positive') return 'text-positive';
		if (tone === 'negative') return 'text-negative';
		return 'text-ink-subtle';
	}

	// Same shared comparison rule as every other delta in the Analytics screens: no compare
	// requested -> show nothing; compare requested but the compare period had 0 mentions (no
	// usable previous value at all) -> "Ново" if this period has a real value, else nothing.
	// Returns {text, tone} instead of a pre-colored string - the formatted text now carries
	// U+2212 for negatives, which a later `.startsWith('-')` check can't recover correctly.
	private pctChange(current?: number, previous?: number | null, hasEnoughCompareData?: boolean): ChangeView {
		if (current == null) return { text: '', tone: 'neutral' };
		const comparison = resolveComparison(isCompareRequested(this.filters), previous, hasEnoughCompareData);
		if (comparison.insufficient) {
			return isNewMetric(current, 0) ? { text: newMetricLabel(this.translate), tone: 'positive' } : { text: '', tone: 'neutral' };
		}
		if (!comparison.show) return { text: '', tone: 'neutral' };
		const prev = comparison.previous as number;
		const diff = current - prev;
		if (isNewMetric(current, prev)) {
			return { text: `${newMetricLabel(this.translate)} (${formatAnalyticsInt(prev, this.lang)} → ${formatAnalyticsInt(current, this.lang)})`, tone: 'positive' };
		}
		const pct = prev > 0 ? Math.round((diff / prev) * 100) : 0;
		return { text: `${formatSignedAuto(pct, this.lang)}% (${formatAnalyticsInt(prev, this.lang)} → ${formatAnalyticsInt(current, this.lang)})`, tone: signTone(diff) };
	}

	get allTopics(): TopicRowView[] {
		return this.combinedRaw.map((t, i) => this.row(t, i)).sort((a, b) => b.mentions - a.mentions);
	}

	// --- KPI row ---

	get topicsTrackedCount(): number {
		return this.combinedRaw.length;
	}

	// CLIPPING-5: these 3 KPIs used to be pooled here from the industry/corporate topic rows -
	// since a mention with 2+ topics appears in 2+ rows, that double (or more) counted it. The
	// backend now computes all three over each tagged mention exactly once, through the same
	// buildFocusKpis Overview's own KPI row uses - so they read straight off the response.
	get avgReachValue(): string {
		const v = this.data?.avgReach?.value;
		return v != null ? `${formatAnalyticsNumber(v, this.lang, 1)}/100` : '—';
	}

	get avgImpactValue(): string {
		const v = this.data?.avgImpact?.value;
		return v != null ? formatSigned(v, 1, this.lang) : '—';
	}

	get netSentimentValue(): string {
		const v = this.data?.netSentiment?.value;
		return v != null ? `${formatSigned(v, 1, this.lang)}%` : '—';
	}

	// "Врз основа на N спомнувања" caption - pooled mention count across every tracked topic
	// (note: a clip with 2+ topics is counted once per topic, same as everywhere else on this
	// screen - see the component's own comment on why topics have no single clean denominator).
	get topicsTotalMentions(): number {
		return this.combinedRaw.reduce((s, t) => s + (t.mentions ?? 0), 0);
	}

	// Пакет А #11: topicsTotalMentions above is the per-topic sum (double-counts a clip tagged
	// with 2 topics) - this is intentional, but captioning it as a plain "Врз основа на N
	// спомнувања" reads as contradicting the KPI cards' real (non-double-counted) mention count.
	// Spell out both numbers so the difference reads as "by design", not as two screens
	// disagreeing about how many mentions there are.
	get topicsSampleNote(): string {
		return this.translate.instant('ANALYTICS_TOPICS.SAMPLE_NOTE_PER_TOPIC', {
			mentions: formatAnalyticsInt(this.topicsTotalMentions, this.lang),
			newsItems: formatAnalyticsInt(this.data?.mentions?.value ?? 0, this.lang),
		});
	}

	// Per-bar tooltip note (reach/impact bar charts): that one topic's own mention count.
	private topicSampleNote(t: TopicRowView | undefined): string {
		if (!t) return '';
		const count = formatAnalyticsInt(t.mentions, this.lang);
		return this.translate.instant('ANALYTICS_TOPICS.SAMPLE_NOTE', { count });
	}

	// --- Widget 1: Top Topics by Mentions ---

	get topTopicsData(): TopicRowView[] {
		return this.allTopics.slice(0, 6);
	}

	get topTopicsChartData() {
		const rows = this.topTopicsData;
		return { labels: rows.map((d) => d.name), datasets: [{ data: rows.map((d) => d.mentions), backgroundColor: rows.map((d) => d.color), borderRadius: 4, barThickness: 14 }] };
	}

	readonly topTopicsChartOptions = {
		indexAxis: 'y' as const,
		responsive: true,
		maintainAspectRatio: false,
		plugins: { legend: { display: false }, tooltip: { backgroundColor: AXIS.tooltipBg, padding: 8, cornerRadius: 10, callbacks: { label: (ctx: any) => ` ${this.translate.instant('ANALYTICS_GLOSSARY.MENTIONS')}: ${formatAnalyticsInt(ctx.parsed.x, this.lang)}` } } },
		scales: {
			x: { grid: { color: AXIS.grid }, ticks: { font: { size: 10 }, color: AXIS.text } },
			y: { grid: { display: false }, ticks: { font: { size: 11, weight: 'bold' as const }, color: AXIS.text } },
		},
	};

	get topTopicsFooter(): string {
		const top = this.topTopicsData[0];
		const total = this.allTopics.reduce((s, t) => s + t.mentions, 0);
		if (!top || total === 0) return this.translate.instant('ANALYTICS_TOPICS.NO_TOPIC_DATA');
		return this.translate.instant('ANALYTICS_TOPICS.TOP_TOPICS_FOOTER', {
			name: top.name,
			percent: formatAnalyticsNumber((top.mentions / total) * 100, this.lang, 1),
			count: formatAnalyticsInt(top.mentions, this.lang),
		});
	}

	// --- Widget 2: Topic trend ---

	get topicTrendChartData() {
		const series = this.data?.trendByTopic ?? [];
		const labels = (series[0]?.points ?? []).map((p) => (p.bucketStart ? formatAnalyticsAxisDate(new Date(p.bucketStart), this.lang) : ''));
		return {
			labels,
			datasets: series.map((s, i) => ({
				label: translateTopic(s.topic, this.translate),
				data: (s.points ?? []).map((p) => p.mentions ?? 0),
				borderColor: PALETTE[i % PALETTE.length],
				backgroundColor: 'transparent',
				borderWidth: i === 0 ? 2.2 : 1.5,
				tension: 0.35,
				pointRadius: 2,
				pointBackgroundColor: PALETTE[i % PALETTE.length],
				fill: false,
			})),
		};
	}

	readonly topicTrendChartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		interaction: { mode: 'index' as const, intersect: false },
		plugins: { legend: { display: false }, tooltip: { backgroundColor: AXIS.tooltipBg, padding: 8, cornerRadius: 10, bodyFont: { size: 10 }, titleFont: { size: 10, weight: 'bold' as const } } },
		scales: {
			x: { grid: { display: false }, ticks: { font: { size: 10 }, color: AXIS.text } },
			y: { grid: { color: AXIS.grid }, ticks: { font: { size: 10 }, color: AXIS.text } },
		},
	};

	get topicTrendLegend(): { name: string; color: string }[] {
		return (this.data?.trendByTopic ?? []).map((s, i) => ({ name: translateTopic(s.topic, this.translate), color: PALETTE[i % PALETTE.length] }));
	}

	// --- Widget 3: Topic SOV ---

	get topicSovChartData() {
		const rows = this.allTopics;
		return { labels: rows.map((d) => d.name), datasets: [{ data: this.combinedRaw.map((t) => t.sharePercent ?? 0), backgroundColor: rows.map((d) => d.color), borderRadius: 4, barThickness: 14 }] };
	}

	readonly topicSovChartOptions = {
		indexAxis: 'y' as const,
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: { display: false },
			tooltip: {
				backgroundColor: AXIS.tooltipBg,
				padding: 8,
				cornerRadius: 10,
				callbacks: { label: (ctx: any) => ` ${this.translate.instant('ANALYTICS_TOPICS.SHARE_TOOLTIP', { percent: formatAnalyticsNumber(ctx.parsed.x, this.lang, 1), count: formatAnalyticsInt(this.allTopics[ctx.dataIndex]?.mentions ?? 0, this.lang) })}` },
			},
		},
		scales: {
			x: { min: 0, grid: { color: AXIS.grid }, ticks: { font: { size: 10 }, color: AXIS.text, callback: (v: number | string) => `${v}%` } },
			y: { grid: { display: false }, ticks: { font: { size: 11, weight: 'bold' as const }, color: AXIS.text } },
		},
	};

	// --- Widget 4: Topic Group Sentiment ---

	get topicGroupChartData() {
		const rows = this.data?.groupSentiment ?? [];
		return {
			labels: rows.map((r) => `${r.name} (${formatAnalyticsInt(r.mentions ?? 0, this.lang)})`),
			datasets: [
				{ label: this.translate.instant('ANALYTICS_GLOSSARY.POSITIVE'), data: rows.map((r) => r.positivePercent ?? 0), backgroundColor: SENTIMENT_COLORS.positive, barThickness: 22 },
				{ label: this.translate.instant('ANALYTICS_GLOSSARY.NEUTRAL'), data: rows.map((r) => r.neutralPercent ?? 0), backgroundColor: SENTIMENT_COLORS.neutral, barThickness: 22 },
				{ label: this.translate.instant('ANALYTICS_GLOSSARY.NEGATIVE'), data: rows.map((r) => r.negativePercent ?? 0), backgroundColor: SENTIMENT_COLORS.negative, barThickness: 22 },
			],
		};
	}

	// --- Widget 5: Mentions by Topic and Brand ---

	get topicBrandColumns(): { key: string; label: string; color: string }[] {
		return (this.data?.topicByBrand?.columns ?? []).map((c) => ({ key: c.key || '', label: c.label || c.key || '', color: brandColor(c.key, this.brandOptions) }));
	}

	get topicBrandChartData() {
		const matrix = this.data?.topicByBrand;
		const rows = matrix?.rows ?? [];
		const cols = this.topicBrandColumns;
		return {
			labels: rows.map((r) => r.rowLabel || ''),
			datasets: cols.map((col) => ({
				label: col.label,
				data: rows.map((r) => {
					const total = r.totalMentions || 1;
					return ((r.cellsByColumnKey?.[col.key]?.mentions ?? 0) / total) * 100;
				}),
				backgroundColor: col.color,
				barThickness: 13,
			})),
		};
	}

	get topicBrandFooter(): string {
		const focus = this.topicBrandColumns[0];
		return focus
			? this.translate.instant('ANALYTICS_TOPICS.TOPIC_BRAND_FOOTER', { brandCount: this.topicBrandColumns.length, topicCount: (this.data?.topicByBrand?.rows ?? []).length })
			: this.translate.instant('ANALYTICS_TOPICS.NO_BRAND_TOPIC_DATA');
	}

	readonly stackedPercentChartOptions = {
		indexAxis: 'y' as const,
		responsive: true,
		maintainAspectRatio: false,
		plugins: { legend: { display: false }, tooltip: { backgroundColor: AXIS.tooltipBg, padding: 8, cornerRadius: 10, callbacks: { label: (ctx: any) => ` ${ctx.dataset.label}: ${formatAnalyticsInt(ctx.parsed.x, this.lang)}%` } } },
		scales: {
			x: { stacked: true, max: 100, grid: { color: AXIS.grid }, ticks: { font: { size: 10 }, color: AXIS.text, callback: (v: number | string) => `${v}%` } },
			y: { stacked: true, grid: { display: false }, ticks: { font: { size: 11, weight: 'bold' as const }, color: AXIS.text } },
		},
	};

	// --- Widget 6: Topic Sentiment Overview ---

	get topicSentimentChartData() {
		const rows = this.allTopics.slice(0, 8);
		const raw = this.combinedRaw.slice(0, 8);
		return {
			labels: rows.map((r) => r.name),
			datasets: [
				{ label: this.translate.instant('ANALYTICS_GLOSSARY.POSITIVE'), data: raw.map((t) => t.positivePercent ?? 0), backgroundColor: SENTIMENT_COLORS.positive, barThickness: 13 },
				{ label: this.translate.instant('ANALYTICS_GLOSSARY.NEUTRAL'), data: raw.map((t) => t.neutralPercent ?? 0), backgroundColor: SENTIMENT_COLORS.neutral, barThickness: 13 },
				{ label: this.translate.instant('ANALYTICS_GLOSSARY.NEGATIVE'), data: raw.map((t) => t.negativePercent ?? 0), backgroundColor: SENTIMENT_COLORS.negative, barThickness: 13 },
			],
		};
	}

	// --- Widget 7/8: Visibility / Impact by topic ---

	// SETTE redesign (CLIPPING-598), Правило А: Visibility (unsigned) rank-list bars are plain
	// blue; Impact (signed) is the one allowed to diverge by sign, via signTone.
	get reachTopicChartData() {
		const rows = [...this.allTopics].sort((a, b) => b.reachValue - a.reachValue);
		return { labels: rows.map((r) => r.name), datasets: [{ data: rows.map((r) => r.reachValue), backgroundColor: '#244896', borderRadius: 4, barThickness: 14 }] };
	}

	readonly reachTopicChartOptions = {
		indexAxis: 'y' as const,
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: { display: false },
			tooltip: {
				backgroundColor: AXIS.tooltipBg,
				padding: 8,
				cornerRadius: 10,
				callbacks: {
					label: (ctx: any) => {
						const rows = [...this.allTopics].sort((a, b) => b.reachValue - a.reachValue);
						return ` ${this.translate.instant('ANALYTICS_TOPICS.AVG_VISIBILITY_TOOLTIP', { value: formatAnalyticsNumber(ctx.parsed.x, this.lang, 1), note: this.topicSampleNote(rows[ctx.dataIndex]) })}`;
					},
				},
			},
		},
		scales: { x: { min: 0, max: 100, grid: { color: AXIS.grid }, ticks: { font: { size: 10 }, color: AXIS.text, stepSize: 20 } }, y: { grid: { display: false }, ticks: { font: { size: 11, weight: 'bold' as const }, color: AXIS.text } } },
	};

	get impactTopicChartData() {
		const rows = [...this.allTopics].sort((a, b) => b.impact - a.impact);
		return { labels: rows.map((r) => r.name), datasets: [{ data: rows.map((r) => r.impact), backgroundColor: rows.map((r) => SENTIMENT_COLORS[r.impactTone]), borderRadius: 4, barThickness: 14 }] };
	}

	readonly impactTopicChartOptions = {
		indexAxis: 'y' as const,
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: { display: false },
			tooltip: {
				backgroundColor: AXIS.tooltipBg,
				padding: 8,
				cornerRadius: 10,
				callbacks: {
					label: (ctx: any) => {
						const rows = [...this.allTopics].sort((a, b) => b.impact - a.impact);
						return ` ${this.translate.instant('ANALYTICS_TOPICS.AVG_IMPACT_TOOLTIP', { value: formatSignedAuto(ctx.parsed.x, this.lang), note: this.topicSampleNote(rows[ctx.dataIndex]) })}`;
					},
				},
			},
		},
		scales: {
			x: { min: -100, max: 100, grid: { color: (ctx: any) => (ctx.tick.value === 0 ? AXIS.baseline : AXIS.grid), lineWidth: (ctx: any) => (ctx.tick.value === 0 ? 1.5 : 1) }, ticks: { font: { size: 10 }, color: AXIS.text, stepSize: 50, callback: (v: number | string) => formatSignedAuto(Number(v), this.lang) } },
			y: { grid: { display: false }, ticks: { font: { size: 11, weight: 'bold' as const }, color: AXIS.text } },
		},
	};

	// --- Widget 9: Top negative topics ---

	get topNegativeTopics(): NegativeTopicRowView[] {
		return this.combinedRaw
			.filter((t) => t.negativePercent != null)
			.map((t) => ({ topic: translateTopic(t.topic, this.translate), negativePct: `${formatAnalyticsNumber(t.negativePercent ?? 0, this.lang, 1)}%`, mentions: formatAnalyticsInt(t.mentions ?? 0, this.lang), sortKey: t.negativePercent ?? 0 }))
			.sort((a: any, b: any) => b.sortKey - a.sortKey)
			.slice(0, 6)
			.map(({ topic, negativePct, mentions }) => ({ topic, negativePct, mentions }));
	}

	// --- Widget 10: Topic drilldown ---

	get drilldownTopics(): { raw: string; label: string }[] {
		return this.combinedRaw.map((t) => ({ raw: t.topic || '', label: translateTopic(t.topic, this.translate) }));
	}

	get selectedDrilldownTopicLabel(): string {
		return translateTopic(this.selectedDrilldownTopic, this.translate);
	}

	get currentDrilldownRaw(): ClientAnalyticsTopicRow | undefined {
		return this.combinedRaw.find((t) => t.topic === this.selectedDrilldownTopic);
	}

	get drilldownMentions(): string {
		return formatAnalyticsInt(this.currentDrilldownRaw?.mentions ?? 0, this.lang);
	}

	get drilldownMentionsChg(): ChangeView {
		const t = this.currentDrilldownRaw;
		return this.pctChange(t?.mentions, t?.previousMentions, t?.hasEnoughCompareData);
	}

	get drilldownReach(): string {
		const v = this.currentDrilldownRaw?.avgReach;
		return v != null ? formatAnalyticsNumber(v, this.lang, 1) : '—';
	}

	get drilldownReachChg(): ChangeView {
		const t = this.currentDrilldownRaw;
		if (!t || t.avgReach == null) return { text: '', tone: 'neutral' };
		const comparison = resolveComparison(isCompareRequested(this.filters), t.previousAvgReach, t.hasEnoughCompareData);
		if (comparison.insufficient) {
			return isNewMetric(t.avgReach, 0) ? { text: newMetricLabel(this.translate), tone: 'positive' } : { text: '', tone: 'neutral' };
		}
		if (!comparison.show) return { text: '', tone: 'neutral' };
		const delta = t.avgReach - (comparison.previous as number);
		return { text: `${formatSigned(delta, 1, this.lang)} pts`, tone: signTone(delta) };
	}

	get drilldownImpact(): string {
		const v = this.currentDrilldownRaw?.avgImpact;
		return v != null ? formatSigned(v, 1, this.lang) : '—';
	}

	get drilldownImpactTone(): Tone {
		return signTone(this.currentDrilldownRaw?.avgImpact ?? 0);
	}

	get drilldownImpactChg(): ChangeView {
		const t = this.currentDrilldownRaw;
		if (!t || t.avgImpact == null) return { text: '', tone: 'neutral' };
		const comparison = resolveComparison(isCompareRequested(this.filters), t.previousAvgImpact, t.hasEnoughCompareData);
		if (comparison.insufficient) {
			return isNewMetric(t.avgImpact, 0) ? { text: newMetricLabel(this.translate), tone: 'positive' } : { text: '', tone: 'neutral' };
		}
		if (!comparison.show) return { text: '', tone: 'neutral' };
		const delta = t.avgImpact - (comparison.previous as number);
		return { text: `${formatSigned(delta, 1, this.lang)} pts`, tone: signTone(delta) };
	}

	get drilldownSentiment(): { pos: number; neu: number; neg: number } {
		const t = this.currentDrilldownRaw;
		return { pos: Math.round(t?.positivePercent ?? 0), neu: Math.round(t?.neutralPercent ?? 0), neg: Math.round(t?.negativePercent ?? 0) };
	}

	get drilldownSentimentChartData() {
		const d = this.drilldownSentiment;
		const labels = [this.translate.instant('ANALYTICS_GLOSSARY.POSITIVE'), this.translate.instant('ANALYTICS_GLOSSARY.NEUTRAL'), this.translate.instant('ANALYTICS_GLOSSARY.NEGATIVE')];
		return { labels, datasets: [{ data: [d.pos, d.neu, d.neg], backgroundColor: [SENTIMENT_COLORS.positive, SENTIMENT_COLORS.neutral, SENTIMENT_COLORS.negative], borderWidth: 2, borderColor: '#ffffff', hoverOffset: 3 }] };
	}

	readonly drilldownSentimentChartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		cutout: '65%',
		plugins: {
			legend: { display: false },
			tooltip: {
				backgroundColor: AXIS.tooltipBg,
				padding: 6,
				cornerRadius: 8,
				callbacks: {
					label: (ctx: any) =>
						` ${this.translate.instant('ANALYTICS_TOPICS.DRILLDOWN_SENTIMENT_TOOLTIP', { label: ctx.label, percent: formatAnalyticsNumber(ctx.parsed, this.lang, 1), count: formatAnalyticsInt(this.currentDrilldownRaw?.mentions ?? 0, this.lang) })}`,
				},
			},
		},
	};
}
